import { ThemeConfig } from '../types/theme';

// "MAIE Classic" — the previous default light palette (indigo primary), now a
// selectable named preset. The Periwinkle theme above is the "MAIE Brand"
// default; this stays in the catalogue (const + id unchanged as `maei-brand`
// so saved themeIds don't break) and themeGenerator derives its neutral light
// base from it.
export const maeiBrandTheme: ThemeConfig = {
  id: 'maei-brand',
  scheme: 'light',
  name: 'MAIE Classic',
  description: 'The previous MAIE palette — indigo primary, beige accent',
  colors: {
    // MAIE brand: blue→indigo blend used across MAIE.Landing.
    // Primary scale is anchored on indigo (the purple-blue end of the blend);
    // a sister `maei-blue` scale lives in index.css for the brand-true blue.
    primary: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
    },
    secondary: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
    },
    accent: '#8b5cf6',
    background: {
      primary: '#ffffff',
      secondary: '#f9fafb',
      tertiary: '#f3f4f6',
    },
    text: {
      primary: '#111827',
      secondary: '#4b5563',
      tertiary: '#6b7280',
      button: '#ffffff',
    },
    border: {
      primary: '#e5e7eb',
      secondary: '#d1d5db',
    },
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#4f46e5',
  },
  font: {
    primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary: 'Georgia, serif',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
};

// Indigo Night — the previous default dark palette, now a selectable named
// preset (Periwinkle Dark is the platform default below). themeGenerator still
// derives its neutral dark base from this palette. The older slate/sky palette
// is preserved separately as `slateDarkTheme`.
export const indigoNightTheme: ThemeConfig = {
  id: 'indigo-night',
  scheme: 'dark',
  name: 'Indigo Night',
  description: 'MAIE brand indigo, tuned for dark surfaces',
  colors: {
    primary: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
    },
    secondary: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
    },
    accent: '#a78bfa',
    background: {
      primary: '#0e1018',
      secondary: '#181b27',
      tertiary: '#232838',
    },
    text: {
      primary: '#e8eaf6',
      secondary: '#b6bcd4',
      tertiary: '#8a90ad',
      button: '#ffffff',
    },
    border: {
      primary: '#2e3450',
      secondary: '#424a6e',
    },
    success: '#34d399',
    warning: '#fbbf24',
    error: '#f87171',
    info: '#818cf8',
  },
  font: {
    primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary: 'Georgia, serif',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
  },
};

export const medicalGreenTheme: ThemeConfig = {
  id: 'medical-green',
  scheme: 'light',
  name: 'Medical Green',
  description: 'A calming green theme for healthcare',
  colors: {
    primary: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
    },
    secondary: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },
    accent: '#14b8a6',
    background: {
      primary: '#ffffff',
      secondary: '#f0fdf4',
      tertiary: '#dcfce7',
    },
    text: {
      primary: '#064e3b',
      secondary: '#047857',
      tertiary: '#059669',
      button: '#ffffff',
    },
    border: {
      primary: '#bbf7d0',
      secondary: '#86efac',
    },
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#06b6d4',
  },
  font: {
    primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary: 'Georgia, serif',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
};

export const professionalPurpleTheme: ThemeConfig = {
  id: 'professional-purple',
  scheme: 'light',
  name: 'Professional Purple',
  description: 'A sophisticated purple theme',
  colors: {
    primary: {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7c3aed',
      800: '#6b21a8',
      900: '#581c87',
    },
    secondary: {
      50: '#fdf4ff',
      100: '#fae8ff',
      200: '#f5d0fe',
      300: '#f0abfc',
      400: '#e879f9',
      500: '#d946ef',
      600: '#c026d3',
      700: '#a21caf',
      800: '#86198f',
      900: '#701a75',
    },
    accent: '#8b5cf6',
    background: {
      primary: '#ffffff',
      secondary: '#faf5ff',
      tertiary: '#f3e8ff',
    },
    text: {
      primary: '#2e1065',
      secondary: '#4c1d95',
      tertiary: '#6b21a8',
      button: '#ffffff',
    },
    border: {
      primary: '#e9d5ff',
      secondary: '#d8b4fe',
    },
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#6366f1',
  },
  font: {
    primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary: 'Georgia, serif',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
};

export const highContrastTheme: ThemeConfig = {
  id: 'high-contrast',
  scheme: 'light',
  name: 'High Contrast',
  description: 'Maximum readability with high contrast',
  colors: {
    primary: {
      50: '#f0f0f0',
      100: '#e0e0e0',
      200: '#c0c0c0',
      300: '#a0a0a0',
      400: '#808080',
      500: '#606060',
      600: '#404040',
      700: '#303030',
      800: '#202020',
      900: '#101010',
    },
    secondary: {
      50: '#ffffff',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
    accent: '#000000',
    background: {
      primary: '#ffffff',
      secondary: '#f5f5f5',
      tertiary: '#eeeeee',
    },
    text: {
      primary: '#000000',
      secondary: '#212121',
      tertiary: '#424242',
      button: '#ffffff',
    },
    border: {
      primary: '#000000',
      secondary: '#424242',
    },
    success: '#008000',
    warning: '#ff8c00',
    error: '#dc143c',
    info: '#0000ff',
  },
  font: {
    primary: 'Arial, sans-serif',
    secondary: 'Georgia, serif',
  },
  borderRadius: {
    sm: '0.125rem',
    md: '0.25rem',
    lg: '0.375rem',
    xl: '0.5rem',
    '2xl': '0.75rem',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
  },
};

export const customTheme: ThemeConfig = {
  id: 'custom',
  scheme: 'light',
  name: 'Custom Theme',
  description: 'Your custom theme configuration',
  colors: {
    primary: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
    },
    secondary: {
      50: '#eef2ff',
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5',
      700: '#4338ca',
      800: '#3730a3',
      900: '#312e81',
    },
    accent: '#8b5cf6',
    background: {
      primary: '#ffffff',
      secondary: '#f9fafb',
      tertiary: '#f3f4f6',
    },
    text: {
      primary: '#111827',
      secondary: '#4b5563',
      tertiary: '#6b7280',
      button: '#ffffff',
    },
    border: {
      primary: '#e5e7eb',
      secondary: '#d1d5db',
    },
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },
  font: {
    primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary: 'Georgia, serif',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
};

// ── Dark catalogue ────────────────────────────────────────────────
// Extra dark presets so the "Dark theme" picker in System Settings →
// Branding offers real variety, mirroring the light catalogue. All
// share the same dark shadow stack as `darkTheme`. Each keeps borders
// clearly visible against its background (no near-invisible 1px lines).
const darkShadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
};

const darkBorderRadius = {
  sm: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
};

const darkFont = {
  primary: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  secondary: 'Georgia, serif',
};

// Slate Dark — the previous default dark palette (sky-blue primary on slate),
// kept as a selectable preset now that Indigo Night is the default.
export const slateDarkTheme: ThemeConfig = {
  id: 'slate-dark',
  scheme: 'dark',
  name: 'Slate Dark',
  description: 'A modern dark theme with proper contrast',
  colors: {
    primary: {
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
    },
    secondary: {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7c3aed',
      800: '#6b21a8',
      900: '#581c87',
    },
    accent: '#a78bfa',
    background: {
      primary: '#0f172a',
      secondary: '#1e293b',
      tertiary: '#334155',
    },
    text: {
      primary: '#f1f5f9',
      secondary: '#cbd5e1',
      tertiary: '#94a3b8',
      button: '#ffffff',
    },
    border: {
      primary: '#334155',
      secondary: '#475569',
    },
    success: '#34d399',
    warning: '#fbbf24',
    error: '#f87171',
    info: '#60a5fa',
  },
  font: darkFont,
  borderRadius: darkBorderRadius,
  shadows: darkShadows,
};

// ── Platform default: Periwinkle ──────────────────────────────────
// The house default light+dark pair — periwinkle primary (#798BF7), peach
// secondary, on a soft alice-blue canvas (light) / near-black (dark). This is
// what a tenant that hasn't customised lands on; MAIE Brand / Indigo Night
// remain in the catalogue as named presets. Scales are anchored so 500 = the
// exact colour shown in the Branding editor and 600 (the button token) reads
// as vivid periwinkle.
// Display name is "MAIE Brand" (this IS the house brand default); the const
// + id stay `periwinkle` so saved tenant themeIds and fallbacks don't break.
export const periwinkleTheme: ThemeConfig = {
  id: 'periwinkle',
  scheme: 'light',
  name: 'MAIE Brand',
  description: 'The MAIE brand palette — periwinkle primary on a soft alice-blue canvas',
  colors: {
    // Brand periwinkle #5868FF (the real MAIE brand value): deep enough that
    // white button text is legible (unlike the lighter #798BF7). 500 stays the
    // brand — it is what `_kit/maie.css` paints .btn-primary and .chip-brand
    // with, so solid brand surfaces are unchanged.
    //
    // 200/300/600/700 now carry the design handoff's own brand ramp
    // (`_kit/maie.css` --brand-200/300/600/700). 600 in particular moves off
    // the brand value to #4553E6, because the kit uses --brand-600 for text on
    // a --brand-100 wash (.chip-soft) where #5868FF is too light to read.
    primary: {
      50: '#eef0ff', // = kit --brand-100
      100: '#e0e4ff',
      200: '#d6dbff', // kit --brand-200
      300: '#b9c2ff', // kit --brand-300
      400: '#808cff',
      500: '#5868ff', // kit --brand-500 — the brand
      600: '#4553e6', // kit --brand-600 — text on brand washes
      700: '#3a45c4', // kit --brand-700
      800: '#2e36a8',
      900: '#1b1f4a',
    },
    secondary: {
      50: '#fffcf9',
      100: '#fffaf4',
      200: '#fff4e6', // kit --beige-100
      300: '#ffeed9',
      400: '#ffe5c7',
      500: '#ffddb7',
      600: '#d6ba9a',
      700: '#ad967c',
      800: '#85735f',
      900: '#5c5042',
    },
    accent: '#c5d3fb',
    // Neutral canvas, per the design handoff (`_kit/maie.css` --surface-0/50/
    // 100). The previous alice-blue ground (#f0f8ff) tinted every surface in
    // the app blue; the canvases sit on plain greys, and the kit's cards
    // (--surface-0) are white against a --surface-50 page. background.primary
    // is the card/sheet ground, .secondary the app canvas.
    background: {
      primary: '#ffffff', // kit --surface-0
      secondary: '#f7f8fa', // kit --surface-50
      tertiary: '#f1f3f6', // kit --surface-100
    },
    text: {
      primary: '#161616', // kit --ink-900
      secondary: '#4f4f4f', // kit --ink-600
      tertiary: '#8a8f98', // kit --ink-400
      // White text on the #5868FF primary button (the token also drives the
      // .text-white utility, so all colored buttons get white labels).
      button: '#ffffff',
    },
    border: {
      primary: '#e3e7ed', // kit --stroke-200
      secondary: '#ccd3dc', // one step down; the kit has a single stroke token
    },
    // Status is functional, not brand — the kit's own semantic set, which is
    // muted against the Tailwind defaults these replace (#10b981 / #f59e0b /
    // #ef4444) so a status chip reads as information rather than alarm.
    success: '#1e9e6a',
    warning: '#b87e14',
    error: '#c0362c',
    info: '#3d5a70',
  },
  font: {
    // The design's stack (`_kit/maie.css`). General Sans ships embedded in
    // src/components/kit/fonts.css; Beiruti is the Arabic face, linked from
    // index.html. Hanken Grotesk is the kit's own fallback, then the system.
    primary: '"General Sans", "Hanken Grotesk", system-ui, sans-serif',
    secondary: '"Beiruti", "General Sans", sans-serif',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
  },
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
};

// Display name "MAIE Brand Dark"; const + id stay `periwinkle-dark`.
export const periwinkleDarkTheme: ThemeConfig = {
  id: 'periwinkle-dark',
  scheme: 'dark',
  name: 'MAIE Brand Dark',
  description: 'The MAIE brand palette on a near-black canvas',
  colors: {
    // Brand periwinkle #5868FF; 600 is the button, white text legible on it
    // over the near-black canvas.
    primary: {
      50: '#eef0ff',
      100: '#e0e4ff',
      200: '#c6ccff',
      300: '#a8b0ff',
      400: '#808cff',
      500: '#5868ff',
      600: '#5868ff',
      700: '#3d4be0',
      800: '#2e36a8',
      900: '#1b1f4a',
    },
    secondary: {
      50: '#fffcf9',
      100: '#fffaf4',
      200: '#fff4e8',
      300: '#ffeed9',
      400: '#ffe5c7',
      500: '#ffddb7',
      600: '#d6ba9a',
      700: '#ad967c',
      800: '#85735f',
      900: '#5c5042',
    },
    accent: '#c5d3fb',
    background: {
      primary: '#0a0a0a',
      secondary: '#15161a',
      tertiary: '#212329',
    },
    text: {
      primary: '#e8eaf6',
      secondary: '#b6bcd4',
      tertiary: '#8a90ad',
      // White on the #5868FF primary button; also drives .text-white.
      button: '#ffffff',
    },
    border: {
      primary: '#2c2e37',
      secondary: '#3f424e',
    },
    success: '#34d399',
    warning: '#fbbf24',
    error: '#f87171',
    info: '#808cff',
  },
  font: darkFont,
  borderRadius: darkBorderRadius,
  shadows: darkShadows,
};

// Platform defaults now point at the Periwinkle pair. Consumers that import
// `defaultTheme` / `darkTheme` (useThemeStore initial state, the catalogue)
// get Periwinkle automatically; themeGenerator deliberately imports the
// neutral MAIE Brand / Indigo Night bases instead (see themeGenerator.ts).
export const defaultTheme: ThemeConfig = periwinkleTheme;
export const darkTheme: ThemeConfig = periwinkleDarkTheme;

export const emeraldDarkTheme: ThemeConfig = {
  id: 'emerald-dark',
  scheme: 'dark',
  name: 'Emerald Night',
  description: 'A calming green dark theme for healthcare',
  colors: {
    primary: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
    },
    secondary: {
      50: '#f0fdf4',
      100: '#dcfce7',
      200: '#bbf7d0',
      300: '#86efac',
      400: '#4ade80',
      500: '#22c55e',
      600: '#16a34a',
      700: '#15803d',
      800: '#166534',
      900: '#14532d',
    },
    accent: '#2dd4bf',
    background: {
      primary: '#0a1411',
      secondary: '#13211c',
      tertiary: '#1d2f28',
    },
    text: {
      primary: '#e7f5ef',
      secondary: '#b3ccc1',
      tertiary: '#86a497',
      button: '#ffffff',
    },
    border: {
      primary: '#234038',
      secondary: '#34614f',
    },
    success: '#34d399',
    warning: '#fbbf24',
    error: '#f87171',
    info: '#5eead4',
  },
  font: darkFont,
  borderRadius: darkBorderRadius,
  shadows: darkShadows,
};

export const royalDarkTheme: ThemeConfig = {
  id: 'royal-dark',
  scheme: 'dark',
  name: 'Royal Plum',
  description: 'A sophisticated purple dark theme',
  colors: {
    primary: {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7c3aed',
      800: '#6b21a8',
      900: '#581c87',
    },
    secondary: {
      50: '#fdf4ff',
      100: '#fae8ff',
      200: '#f5d0fe',
      300: '#f0abfc',
      400: '#e879f9',
      500: '#d946ef',
      600: '#c026d3',
      700: '#a21caf',
      800: '#86198f',
      900: '#701a75',
    },
    accent: '#e879f9',
    background: {
      primary: '#120c1a',
      secondary: '#1d1428',
      tertiary: '#2b1d3d',
    },
    text: {
      primary: '#f3e9fb',
      secondary: '#cbb6dd',
      tertiary: '#a187b8',
      button: '#ffffff',
    },
    border: {
      primary: '#3a2a52',
      secondary: '#533c73',
    },
    success: '#34d399',
    warning: '#fbbf24',
    error: '#f87171',
    info: '#c084fc',
  },
  font: darkFont,
  borderRadius: darkBorderRadius,
  shadows: darkShadows,
};

export const carbonTheme: ThemeConfig = {
  id: 'carbon-contrast',
  scheme: 'dark',
  name: 'Carbon (High Contrast)',
  description: 'Maximum readability on a near-black surface',
  colors: {
    primary: {
      50: '#f0f9ff',
      100: '#e0f2fe',
      200: '#bae6fd',
      300: '#7dd3fc',
      400: '#38bdf8',
      500: '#0ea5e9',
      600: '#0284c7',
      700: '#0369a1',
      800: '#075985',
      900: '#0c4a6e',
    },
    secondary: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f',
    },
    accent: '#fbbf24',
    background: {
      primary: '#000000',
      secondary: '#0a0a0a',
      tertiary: '#1a1a1a',
    },
    text: {
      primary: '#ffffff',
      secondary: '#e5e5e5',
      tertiary: '#c0c0c0',
      button: '#000000',
    },
    border: {
      primary: '#6b7280',
      secondary: '#9ca3af',
    },
    success: '#4ade80',
    warning: '#fbbf24',
    error: '#f87171',
    info: '#60a5fa',
  },
  font: {
    primary: 'Arial, sans-serif',
    secondary: 'Georgia, serif',
  },
  borderRadius: {
    sm: '0.125rem',
    md: '0.25rem',
    lg: '0.375rem',
    xl: '0.5rem',
    '2xl': '0.75rem',
  },
  shadows: darkShadows,
};

// Dark-slot counterpart of `customTheme`: lets an owner hand-tune a dark
// palette (mirrors the light custom editor). Seeded from the platform dark
// theme so the editor opens on a sensible dark base instead of black.
export const customDarkTheme: ThemeConfig = {
  ...darkTheme,
  id: 'custom-dark',
  scheme: 'dark',
  name: 'Custom Dark',
  description: 'Your custom dark theme configuration',
};

export const themes: ThemeConfig[] = [
  defaultTheme, // Periwinkle (platform default light)
  darkTheme, // Periwinkle Dark (platform default dark)
  maeiBrandTheme, // former default — now a named preset
  indigoNightTheme, // former default dark — now a named preset
  medicalGreenTheme,
  professionalPurpleTheme,
  highContrastTheme,
  customTheme,
  slateDarkTheme,
  emeraldDarkTheme,
  royalDarkTheme,
  carbonTheme,
  customDarkTheme,
];